import { useState, useEffect, useRef } from 'react';

export default function DNASection() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeValue, setActiveValue] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  const values = [
    {
      icon: 'ri-lightbulb-line',
      title: 'Passion for Continuous Innovation',
      description: 'We\'re driven by an insatiable curiosity for what\'s new and next. From the latest design trends to groundbreaking AI, we constantly explore and integrate cutting-edge tools.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Human-Centric Design',
      description: 'Every digital experience we craft prioritizes the human element. We believe that true impact comes when Function, Form, and Feeling are perfectly balanced.',
      color: 'from-red-500 to-pink-500'
    },
    {
      icon: 'ri-book-open-line',
      title: 'Strategic Storytelling',
      description: 'We transform brands into compelling narratives that don\'t just capture attention but resonate deeply.',
      color: 'from-pink-500 to-purple-500'
    },
    {
      icon: 'ri-bar-chart-box-line',
      title: 'Data-Informed Creativity',
      description: 'Our creativity isn\'t left to chance. We blend artistic vision with intelligent analytics for tangible impact.',
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: 'ri-shake-hands-line',
      title: 'Empowering Partnership',
      description: 'We see ourselves as an extension of your team. Fostering close collaboration, transparency, and knowledge sharing.',
      color: 'from-blue-500 to-orange-500'
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-20 bg-gradient-to-b from-gray-900 to-black relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              The Hash Adz DNA:
            </span>
            <br />
            <span className="text-white">What Drives Us</span>
          </h2>

          <div className="max-w-4xl mx-auto mb-8">
            <blockquote className="text-2xl md:text-4xl font-bold text-orange-400 mb-6 italic">
              "Designs are in our blood. We bleed freshness."
            </blockquote>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              Since 2014, Hash Adz has been an ever-evolving force in design, storytelling, and digital strategy.
            </p>
          </div>

          <p className="text-base md:text-lg text-gray-400 max-w-5xl mx-auto leading-relaxed">
            From our earliest days, we've seen ourselves as more than just service providers. We are storytellers 
            transforming complex ideas into clear narratives, visual alchemists turning concepts into captivating designs, 
            strategic thinkers charting pathways to growth, and now, architects of intelligent digital solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className={`space-y-6 transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            {values.map((value, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border transition-all duration-300 cursor-pointer ${
                  activeValue === index
                    ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/50'
                    : 'bg-gray-800/50 border-gray-700 hover:border-orange-500/30'
                }`}
                onClick={() => setActiveValue(index)}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-r ${value.color} flex-shrink-0`}>
                    <i className={`${value.icon} text-white text-xl`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
                    <p className="text-gray-300 leading-relaxed text-sm">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={`hidden lg:block sticky top-24 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/assets/dna1.jpg"
                alt="Hash Adz DNA"
                className="w-full h-[600px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
              
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-3xl font-bold text-white mb-2">{values[activeValue].title}</h3>
                <p className="text-gray-200">{values[activeValue].description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}